/**
 * "Shrine travel" (AD-672 S4, `q-shard-sink-shrine-travel`) — the pure half of
 * the world panel's travel row. From the zone's shrines (the join ack's world
 * descriptor) and the player's own position it finds the shrine the player
 * stands at (within the server's 6 m reach) and lists every OTHER shrine of the
 * zone as a destination, nearest first (ties by id, so the list is stable).
 *
 * It decides NOTHING the server does not re-check: the origin is found again
 * server-side from the authoritative position, and the fee (a server-config
 * value the client does not hold), combat and the balance are the commit's.
 * The toast after a trip names the fee. Pure; node-testable.
 */

/** The server's shrine reach (m) — mirrors `SHRINE_INTERACT_RANGE_M`. */
export const SHRINE_TRAVEL_REACH_M = 6;

export interface ShrineAt {
  id: string;
  name?: string;
  x: number;
  z: number;
}

export interface ShrineTravelOptions<T extends ShrineAt> {
  /** The shrine the player stands at. */
  here: T;
  /** Every other shrine of the zone, nearest to `here` first, with its distance (m). */
  to: { shrine: T; distanceM: number }[];
}

export function shrineTravelOptions<T extends ShrineAt>(
  shrines: readonly T[],
  self: { x: number; z: number } | null,
  reachM: number = SHRINE_TRAVEL_REACH_M,
): ShrineTravelOptions<T> | null {
  if (!self) return null;
  let here: T | undefined;
  let hereD = Infinity;
  for (const sh of shrines) {
    const d = Math.hypot(sh.x - self.x, sh.z - self.z);
    if (d <= reachM && d < hereD) {
      here = sh;
      hereD = d;
    }
  }
  if (!here) return null;
  const from = here;
  const to = shrines
    .filter((sh) => sh.id !== from.id)
    .map((shrine) => ({ shrine, distanceM: Math.hypot(shrine.x - from.x, shrine.z - from.z) }))
    .sort((a, b) => a.distanceM - b.distanceM || a.shrine.id.localeCompare(b.shrine.id));
  return { here: from, to };
}

/** A shrine's display name: its authored name, else its id. */
export function shrineLabel(shrine: ShrineAt): string {
  return shrine.name ?? shrine.id;
}

/** The toast line for a `travelResult` refusal (the typed reason, in words). */
export function describeTravelDenial(denied: string): string {
  switch (denied) {
    case 'insufficientShards':
      return 'You cannot pay the shrine fee.';
    case 'inCombat':
      return 'The shrine will not carry you out of a fight.';
    case 'downed':
      return 'You cannot travel while down.';
    case 'notNearShrine':
      return 'Stand at a shrine to travel.';
    case 'sameShrine':
      return 'You are already here.';
    default:
      return 'That shrine is not reachable from here.';
  }
}
